import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CalendarDays, ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { fetchEventsPageSettings, fetchPublishedEvents } from "@/services/eventService";
import { useLanguage } from "@/context/LanguageContext";

const Events = () => {
  const { language, tl } = useLanguage();
  const { data: events = [], isLoading } = useQuery({
    queryKey: ["events", "published"],
    queryFn: fetchPublishedEvents,
  });
  const { data: heroSettings } = useQuery({
    queryKey: ["events", "page-settings"],
    queryFn: fetchEventsPageSettings,
  });
  const latestEvent = events[0];
  const heroBackgroundImage =
    heroSettings?.heroBackgroundImage || latestEvent?.coverImage || latestEvent?.images[0]?.url || "/placeholder.svg";

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-white to-gray-50">
      <Helmet>
        <title>
          {tl('Evenements | Meubles Karim', 'Events | Meubles Karim', 'Eventi | Meubles Karim')}
        </title>
      </Helmet>

      <section className="relative h-[42vh] md:h-[52vh] overflow-hidden">
        <img
          src={heroBackgroundImage}
          alt={latestEvent?.images[0]?.alt[language] ?? "Event hero"}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 container-custom flex flex-col justify-center">
          <p className="text-xs md:text-sm uppercase tracking-[0.4em] text-white/80 mb-3">
            {tl(
              heroSettings?.heroEyebrowFr || 'Experience Meubles Karim',
              heroSettings?.heroEyebrowEn || 'Experience Meubles Karim',
              heroSettings?.heroEyebrowEn || 'Experience Meubles Karim'
            )}
          </p>
          <h1 className="max-w-4xl text-4xl md:text-6xl text-white font-bold leading-tight">
            {tl(
              heroSettings?.heroTitleFr || 'Nos Evenements Signature',
              heroSettings?.heroTitleEn || 'Our Signature Events',
              heroSettings?.heroTitleEn || 'I nostri eventi signature'
            )}
          </h1>
          <p className="mt-4 max-w-2xl text-sm md:text-lg text-white/90">
            {tl(
              heroSettings?.heroSubtitleFr ||
                "Decouvrez nos moments d'exception: lancements, showcases prives et experiences immersives.",
              heroSettings?.heroSubtitleEn ||
                'Discover our standout moments: launches, private showcases, and immersive design experiences.',
              heroSettings?.heroSubtitleEn ||
                "Scoprite i nostri momenti d'eccezione: lanci, showcase privati ed esperienze immersive."
            )}
          </p>
        </div>
      </section>

      <section className="container-custom py-14 md:py-20">
        <div className="mb-10 md:mb-14 flex items-end justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-2">
              {tl('Chronologie', 'Timeline', 'Cronologia')}
            </p>
            <h2 className="text-3xl md:text-5xl text-gray-900 font-normal">
              {tl('Derniers evenements', 'Latest events', 'Ultimi eventi')}
            </h2>
          </div>
          
        </div>

        {isLoading ? (
          <div className="py-16 text-center text-gray-500">
            {tl('Chargement des evenements...', 'Loading events...', 'Caricamento eventi...')}
          </div>
        ) : events.length === 0 ? (
          <div className="py-16 text-center text-gray-500">
            {tl(
              'Aucun evenement publie pour le moment.',
              'No published events at the moment.',
              'Nessun evento pubblicato al momento.'
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-7">
          {events.map((event) => (
            <motion.article
              key={event.id}
              className="group bg-white border border-gray-200 rounded-xl overflow-hidden shadow-soft hover:shadow-hover transition-all duration-300"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.5 }}
            >
              <Link to={`/events/${event.slug}`} className="block">
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={event.coverImage || event.images[0]?.url || "/placeholder.svg"}
                    alt={event.images[0]?.alt[language] ?? event.title[language]}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
              </Link>

              <div className="p-6">
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-gray-500">
                  <CalendarDays className="h-4 w-4" />
                  <time dateTime={event.date}>
                    {new Date(event.date).toLocaleDateString(language === 'fr' ? 'fr-FR' : language === 'it' ? 'it-IT' : 'en-GB', {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </time>
                </div>

                <h3 className="mt-3 text-2xl leading-tight text-gray-900 font-normal">
                  {event.title[language]}
                </h3>

                <p className="mt-3 text-gray-600 leading-relaxed">
                  {event.shortDescription[language]}
                </p>

                <Link
                  to={`/events/${event.slug}`}
                  className="mt-6 inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-gray-900 hover:text-black"
                >
                  {tl("Voir l'evenement", "View event", "Vedi l'evento")}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </motion.article>
          ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Events;
